import { useCallback, useEffect } from 'react';

/**
 * Custom hook for panning and zooming logic in the Node Editor
 */
export const usePanningAndZoom = ({
  isPanning,
  setIsPanning,
  lastPanPoint,
  setLastPanPoint,
  panOffset,
  setPanOffset,
  zoom,
  setZoom,
  lockedZoomCenter,
  setLockedZoomCenter,
  flowAreaRef,
  currentAction,
  setCurrentAction,
  ACTION_STATE
}) => {
  
  const handleMouseDown = (e) => {
    // Only start panning if we're in IDLE state
    if (currentAction && ACTION_STATE && currentAction !== ACTION_STATE.IDLE) {
      return;
    }
    
    // Only start panning if clicking directly on the canvas background (not on panels or components)
    const isPanel = e.target.closest('.flow-panel');
    const isComponent = e.target.closest('.component-item');
    
    if (isPanel || isComponent) {
      // Don't start panning when clicking on panels or components
      return;
    }

    if (e.button === 0) {
      if (setCurrentAction && ACTION_STATE) {
        setCurrentAction(ACTION_STATE.PANNING);
      }
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;

    const dx = e.clientX - lastPanPoint.x;
    const dy = e.clientY - lastPanPoint.y;

    setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    setLastPanPoint({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
    if (setCurrentAction && ACTION_STATE) {
      setCurrentAction(ACTION_STATE.IDLE);
    }
  };

  const handleSliderZoom = useCallback((newZoom) => {
    if (!flowAreaRef.current) return;
    const rect = flowAreaRef.current.getBoundingClientRect();
    
    // Lock zoom center at middle of viewport if not already locked
    if (!lockedZoomCenter) {
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      setLockedZoomCenter({ x: centerX, y: centerY });
      handleLockedZoom(newZoom, { x: centerX, y: centerY });
    } else {
      handleLockedZoom(newZoom, lockedZoomCenter);
    }
  }, [lockedZoomCenter, flowAreaRef]);

  const handleLockedZoom = useCallback((newZoom, lockedCenter) => {
    const zoomFactor = newZoom / zoom;
    const { x: centerX, y: centerY } = lockedCenter;
    const worldX = (centerX - panOffset.x) / zoom;
    const worldY = (centerY - panOffset.y) / zoom;
    setPanOffset({ x: centerX - worldX * newZoom, y: centerY - worldY * newZoom });
    setZoom(newZoom);
  }, [zoom, panOffset, setPanOffset, setZoom]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (!flowAreaRef.current) return;
    const rect = flowAreaRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const delta = e.deltaY < 0 ? 1.1 : 0.9;
    const newZoom = Math.max(0.5, Math.min(2, zoom * delta));
    const zoomFactor = newZoom / zoom;
    const worldX = (mouseX - panOffset.x) / zoom;
    const worldY = (mouseY - panOffset.y) / zoom;
    setPanOffset({ x: mouseX - worldX * newZoom, y: mouseY - worldY * newZoom });
    setZoom(newZoom);
  }, [zoom, panOffset, setPanOffset, setZoom, flowAreaRef]);

  // Set up global mouse event listeners for panning
  useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isPanning, handleMouseMove, handleMouseUp]);

  // Set up wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const element = flowAreaRef.current;
    if (!element) return;

    element.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      element.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, flowAreaRef]);

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleSliderZoom,
    handleLockedZoom,
    handleWheel,
  };
};
